import {ColorSchemeType} from 'platform/components';
import {match} from 'ts-pattern';

import {AccountingProcessResponseBody} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {
  AccountingProcessMilestone,
  getAccountingProcessMilestone,
} from './getAccountingProcessMilestone';

interface AccountingProcessFlagProps {
  colorScheme: ColorSchemeType;
  label: string;
}

/**
 * Turns an external accounting process into the flag shown on the payment.
 *
 * Blue reads as "in flight", orange as "not final yet", teal and green as the two
 * kinds of done, red and grey as the two kinds of stopped — so the colour alone
 * answers "is this still moving?" without reading the label.
 */
export const getAccountingProcessFlagProps = (
  accountingProcess: AccountingProcessResponseBody
): AccountingProcessFlagProps =>
  match<AccountingProcessMilestone, AccountingProcessFlagProps>(
    getAccountingProcessMilestone(accountingProcess)
  )
    .with('failed', () => ({
      colorScheme: 'red',
      label: i18n.t('entity.checkout.labels.accountingFailed'),
    }))
    .with('canceled', () => ({
      colorScheme: 'neutral',
      label: i18n.t('entity.checkout.labels.accountingCanceled'),
    }))
    .with('documentAvailable', () => ({
      colorScheme: 'green',
      label: i18n.t('entity.checkout.labels.accountingDocumentAvailable'),
    }))
    .with('issued', () => ({
      colorScheme: 'teal',
      label: i18n.t('entity.checkout.labels.accountingIssued'),
    }))
    .with('draft', () => ({
      colorScheme: 'orange',
      label: i18n.t('entity.checkout.labels.accountingDraft'),
    }))
    .with('accepted', () => ({
      colorScheme: 'blue',
      label: i18n.t('entity.checkout.labels.accountingAccepted'),
    }))
    .with('waiting', () => ({
      colorScheme: 'blue',
      label: i18n.t('entity.checkout.labels.accountingWaiting'),
    }))
    .exhaustive();
